import {type CellAddress, type ItemId, useBoardCard} from '@github-ui/board-core'
import {useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './BoardCardFrame.module.css'

/**
 * Where a card sits in its cell's cards, for a cell that draws only some of them.
 *
 * One value rather than two props, because the two numbers are only ever right together.
 * A position stated without a size is worse than neither: assistive technology resolves it
 * against the items it can see, which in a windowed list is exactly the wrong denominator,
 * so a card would be announced as the third of twelve rather than the two hundredth of
 * eight hundred.
 */
export type BoardCardPlace = {
  /** Zero-based index among the cards the cell has loaded. */
  index: number
  /**
   * How many cards are in the set, as `aria-setsize` means it: every card the cell has,
   * not every card it has loaded, and `-1` when the total is not known.
   *
   * A cell pages, so those are usually different numbers. `BoardCellFrame` works this out
   * from the count the caller stated for the cell; a caller assembling its own does the
   * same.
   */
  setSize: number
  /**
   * Registers this card with its cell's window, which measures it rather than assuming a
   * height. The window reads {@link BoardCardPlace.index} back off the element to know
   * which card it measured, which is why the two arrive together.
   */
  measureRef?: (node: Element | null) => (() => void) | undefined
}

export type BoardCardFrameProps = {
  itemId: ItemId
  /** The cell this card is in. The same item may sit in two cells on a multi-value board. */
  address: CellAddress
  /**
   * Where this card sits among the cell's cards, and how many there are.
   *
   * Stated rather than left to the browser because a cell windows its cards: only the ones
   * on screen are in the page, so a cell holding five hundred would otherwise announce each
   * card as one of the twelve that happen to be drawn.
   *
   * Optional, because a caller assembling its own cell out of these frames may window
   * nothing, and a list that draws every one of its items needs neither number.
   */
  place?: BoardCardPlace
  children?: ReactNode
  className?: string
}

/**
 * A card's shell: border, radius, padding.
 *
 * It is a component rather than something the cell renders inline because it calls its
 * own hook, and a hook cannot be called inside a callback that runs once per card. That
 * is how the roving tabindex, the selection gesture and the drag source all arrived
 * without any caller changing.
 */
export function BoardCardFrame({itemId, address, place, children, className}: BoardCardFrameProps) {
  const {cardProps, isSelected, isDragging, dragRefusal} = useBoardCard(itemId, address)
  const ref = useMergedRefs(cardProps.ref, place?.measureRef)

  return (
    <li
      {...cardProps}
      ref={ref}
      // Read back by the cell's window to know which card a measurement came from. It is
      // here rather than in `cardProps` because it describes where this element sits in a
      // drawn list, which is the styled layer's business rather than the headless one's.
      data-index={place?.index}
      // A styling hook and nothing else. This package draws a selected card no differently
      // from any other, because selection has no treatment yet and inventing one is not
      // this release's to make; a caller that acts on a selection styles this in the
      // meantime. Absent rather than `false`, so `[data-selected]` matches the selected
      // cards and only those.
      data-selected={isSelected ? '' : undefined}
      // The card a pointer has hold of, which this package rings and leaves exactly where
      // it is: `BoardDraggedCard` draws the copy that follows the pointer. On from the
      // press, so the ring answers the grab rather than the movement after it. Absent
      // rather than `false`, on the same rule `data-selected` follows above.
      data-dragging={isDragging ? '' : undefined}
      // The card whose drag the caller refused, which stops it offering a `cursor: grab`
      // it cannot honour and gives a caller somewhere to say more. Absent rather than
      // `false`, on the same rule the two above follow.
      //
      // One attribute rather than a `data-draggable` beside it: two attributes describing
      // one boolean is two things to keep in step. The negative is the one that carries a
      // fact, because it is there exactly when the caller has a reason -- `dragRefusal`,
      // which is what a caller reads to say what that reason was.
      data-drag-disabled={dragRefusal === undefined ? undefined : ''}
      // Both or neither, which is what makes them one prop.
      aria-posinset={place === undefined ? undefined : place.index + 1}
      aria-setsize={place?.setSize}
      className={clsx(styles.CardFrame, className)}
    >
      {children}
    </li>
  )
}
